import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Spin, Alert, message } from 'antd';
import { supabase } from '@/shared/lib/supabase';
import { FormRenderer } from '../components/FormRenderer';
import { FormField } from '../../admin/components/FormBuilder';

interface FormData {
  id: string;
  name: string;
  description: string | null;
  fields: FormField[];
  settings: {
    submitButtonText?: string;
    successMessage?: string;
  };
}

export const ClientFormPage = () => {
  const { clientProductId } = useParams<{ clientProductId: string }>();
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState<FormData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadForm();
  }, [clientProductId]);

  const loadForm = async () => {
    try {
      setLoading(true);
      
      // Get client product and its form
      const { data: clientProduct, error: cpError } = await supabase
        .from('client_products')
        .select(`
          id,
          form_id,
          product:products (
            id,
            form_id
          )
        `)
        .eq('id', clientProductId)
        .single();

      if (cpError) throw cpError;

      // Determine which form to use (client_products.form_id || product.form_id)
      const productData = clientProduct.product as any;
      const formId = clientProduct.form_id || productData?.form_id;

      if (!formId) {
        setError('Nenhum formulário associado a este produto');
        return;
      }

      // Load form
      const { data: form, error: formError } = await supabase
        .from('forms')
        .select('*')
        .eq('id', formId)
        .single();

      if (formError) throw formError;

      setFormData(form);
    } catch (err: any) {
      console.error('Error loading form:', err);
      setError(err.message || 'Erro ao carregar formulário');
      message.error('Erro ao carregar formulário');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 20px' }}>
        <Spin size="large" />
        <p style={{ marginTop: 16 }}>Carregando formulário...</p>
      </div>
    );
  }

  if (error || !formData) {
    return (
      <div style={{ maxWidth: 600, margin: '50px auto', padding: '0 20px' }}>
        <Alert
          message="Erro"
          description={error || 'Formulário não encontrado'}
          type="error"
          showIcon
        />
      </div>
    );
  }

  return (
    <Card
      title={formData.name}
      style={{ maxWidth: 800, margin: '20px auto' }}
    >
      {formData.description && (
        <p style={{ marginBottom: 24, color: '#666' }}>{formData.description}</p>
      )}
      <FormRenderer
        formId={formData.id}
        formName={formData.name}
        fields={formData.fields}
        settings={formData.settings}
        clientProductId={clientProductId!}
      />
    </Card>
  );
};
